import React from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Checkbox from '@amzn/awsui-components-core/checkbox';
import Input from '@amzn/awsui-components-core/input';
import Spinner from '@amzn/awsui-components-core/spinner';
import { AgGridReact } from 'ag-grid-react';
import { managedReportState, useManageReports } from 'src/components/star-grid/hooks/useManageReports';
import { useManageReportsGrid, type ManageReportsActionParams } from 'src/components/star-grid/hooks/useManageReportsGrid';
import type { PnlReportSummary } from 'src/components/star-grid/starGridTypes';
import styles from 'src/components/star-grid/views/styles/ManageReportsView.module.scss';
import 'src/components/star-grid/views/styles/manage-reports-grid.scss';

/**
 * Manage Reports — every report row in Postgres and what state it is in.
 *
 * Deactivating hides a report from the pickers and is reversible; deleting marks it inactive and
 * soft-deleted. Both record who did it, which is why the grid shows the editor alongside the state:
 * a hidden report with no attribution is indistinguishable from one that failed to build.
 */
export const ManageReportsView: React.FC = () => {
  const state = useManageReports();
  // Registered as a named renderer so the column definition stays a plain object (no closure over
  // component state), which keeps AG Grid from rebuilding every column on each action.
  const components = React.useMemo(
    () => ({
      manageReportsActions: (params: ManageReportsActionParams) => {
        const report = params.data;
        if (!report) return null;
        const current = managedReportState(report);
        // A deleted report offers nothing: there is no restore path yet, and a button that cannot
        // finish the job is worse than its absence.
        if (current === 'deleted') return <span className={styles.noActions}>—</span>;
        const busy = state.busyId === report.pnl_report_id;
        return (
          <div className={styles.rowActions}>
            <Button
              variant="inline-link"
              disabled={busy}
              onClick={() => state.setActive(report.pnl_report_id, current !== 'active')}
              data-testid={`manage-reports-toggle-${report.pnl_report_id}`}
            >
              {current === 'active' ? 'Deactivate' : 'Reactivate'}
            </Button>
            <Button
              variant="inline-link"
              disabled={busy}
              onClick={() => state.remove(report.pnl_report_id)}
              data-testid={`manage-reports-delete-${report.pnl_report_id}`}
            >
              Delete
            </Button>
          </div>
        );
      }
    }),
    [state]
  );
  const grid = useManageReportsGrid({ cellRenderer: 'manageReportsActions' });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Manage Reports</h1>
          <p className={styles.subtitle}>
            {state.counts.active} active · {state.counts.deactivated} deactivated · {state.counts.deleted} deleted
          </p>
        </div>
        <div className={styles.headerActions}>
          <Input
            value={state.search}
            onChange={({ detail }) => state.setSearch(detail.value)}
            type="search"
            placeholder="Search Reports"
            ariaLabel="Search Reports"
          />
          <Checkbox checked={state.showDeleted} onChange={({ detail }) => state.setShowDeleted(detail.checked)}>
            Show Deleted Reports
          </Checkbox>
          <Button iconName="refresh" loading={state.loading} onClick={state.refresh} data-testid="manage-reports-refresh">
            Refresh
          </Button>
        </div>
      </header>

      {state.error && (
        <Alert type="error" header="Could Not Load Reports">
          {state.error}
        </Alert>
      )}

      {state.loading && !state.reports.length ? (
        <Box textAlign="center" padding="l">
          <Spinner /> Loading reports…
        </Box>
      ) : (
        <div className={styles.grid} data-testid="manage-reports-grid">
          <AgGridReact<PnlReportSummary>
            theme={grid.theme}
            rowData={[...state.reports]}
            columnDefs={grid.columns}
            components={components}
            defaultColDef={{ sortable: true, resizable: true, suppressMovable: true, filter: true }}
            getRowId={({ data }) => String(data.pnl_report_id)}
            quickFilterText={state.search}
            domLayout="autoHeight"
            rowHeight={34}
            headerHeight={36}
            getRowClass={({ data }) => (data && managedReportState(data) === 'deleted' ? 'sg-manage-deleted-row' : '')}
          />
        </div>
      )}
      {!state.loading && !state.reports.length && <Alert>No reports yet.</Alert>}
    </div>
  );
};

export default ManageReportsView;
